import React, { useMemo, useState } from 'react';
import { Platform, StyleSheet, TextInput, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import useResponsiveness from '@/hooks/useResponsiveness';

// Match the main composer textarea exactly so the inline row reads as a
// peer to the message field, not a sibling. These mirror the values in
// `ChatInputFloating.styles.textarea`:
//   fontSize: 17
//   lineHeight: 24
//   color (text + placeholder) come from the theme tokens used there.
const getInputTextSize = () => 17;
const INPUT_LINE_HEIGHT = 24;

/**
 * Pulls the bits the renderer needs out of the param object so the view
 * stays focused on visuals. Mirrors the pattern used in
 * `FileParamGhostCard` so refactors stay symmetrical between the two
 * "promoted" composer affordances.
 */
const useDerived = (param) => {
  const minLen = param?.validation?.string?.minLength;
  const maxLen = param?.validation?.string?.maxLength;
  const value = typeof param?.value === 'string' ? param.value : '';
  const currentLen = value.length;
  const isRequired = !!param?.validation?.required;

  return useMemo(() => ({
    value,
    currentLen,
    minLen,
    maxLen,
    isRequired,
    showCounter: !!(maxLen || minLen),
    displayName: param?.ui?.displayName || param?.name || 'Detail',
    placeholder: param?.ui?.placeholder || param?.ui?.description || '',
  }), [
    value, currentLen, minLen, maxLen, isRequired,
    param?.ui?.displayName, param?.ui?.description,
    param?.ui?.placeholder, param?.name,
  ]);
};

const createStyles = (colors, isMissing, isXS) => StyleSheet.create({
  container: {
    width: '100%',
    paddingHorizontal: 16,
    paddingVertical: 6,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  label: {
    color: colors.text_color_tertiary,
    fontSize: 12,
    fontStyle: 'italic',
    fontWeight: '500',
  },
  // Asterisk gets unitalicized so it doesn't look like a typo glitch
  // sitting next to the italic label.
  requiredAsterisk: { color: colors.color_destructive, fontStyle: 'normal' },
  counter: {
    color: colors.text_color_tertiary,
    fontSize: 11,
    fontVariant: ['tabular-nums'],
  },
  textarea: {
    color: colors.text_color_primary,
    fontSize: getInputTextSize(isXS),
    lineHeight: INPUT_LINE_HEIGHT,
    paddingVertical: 4,
    paddingHorizontal: 0,
    minHeight: 28,
    maxHeight: 96,
    width: '100%',
    ...Platform.select({
      web: { border: 'none', outlineStyle: 'none', resize: 'none', backgroundColor: 'transparent' },
    }),
  },
});

/**
 * Compact, in-composer control for a "promoted" custom parameter
 * (`ui.displayPosition === 'inline'`). Renders a small italic hint
 * label above a borderless textarea — zero chrome, just typography
 * separating it from the main message field.
 *
 * "Missing" state (red label) only triggers after blur of a required
 * empty field, so it doesn't flash the moment the composer mounts.
 *
 * State is bound to `param.value` via `onChange`, identical to the
 * bar-rendered controls; this is purely a visual relocation.
 */
const InlineParamControl = ({ param, onChange, autoFocus = false }) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const [isFocused, setIsFocused] = useState(false);
  const derived = useDerived(param);

  const { value, currentLen, maxLen, minLen, isRequired, displayName, placeholder, showCounter } = derived;
  const isMissing = isRequired && currentLen === 0 && !isFocused;
  const styles = useMemo(
    () => createStyles(colors, isMissing, isXS),
    [colors, isMissing, isXS]
  );

  const handleChange = (next) => {
    if (typeof onChange === 'function') onChange(next);
  };

  return (
    <View style={styles.container}>
      <View style={styles.labelRow}>
        <Text style={styles.label} numberOfLines={1}>
          {displayName}
          {isRequired ? <Text style={styles.requiredAsterisk}> *</Text> : null}
        </Text>
        {showCounter && (
          <Text style={styles.counter}>
            {`${currentLen}${maxLen ? ` / ${maxLen}` : ''}`}
          </Text>
        )}
      </View>
      <TextInput
        value={value}
        onChangeText={handleChange}
        placeholder={placeholder}
        placeholderTextColor={colors.text_color_tertiary}
        style={styles.textarea}
        multiline
        autoFocus={autoFocus}
        maxLength={maxLen || undefined}
        textAlignVertical="top"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        {...Platform.select({ web: { minLength: minLen || undefined } })}
      />
    </View>
  );
};

export default React.memo(InlineParamControl);
